import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  Area,
  ReferenceLine,
  ReferenceDot,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { BUN_CHART_FONT } from './chartTheme';
import ChartTooltip from './ChartTooltip';

const COLORS = ['var(--bun-data-violet)', 'var(--bun-data-sky)', 'var(--bun-data-amber)'];

/** Presentation only: callers retain period selection, aggregation and formatting. */
export default function SeriesChart({
  data = [],
  type = 'line',
  stacked = true,
  height = 200,
  valueFormatter,
  labelFormatter,
  xTickFormatter,
  yAxisFormatter,
  yDomain,
  tooltipRows,
  referenceLines = [],
  referenceDots = [],
  showLegend = true,
  onPointClick,
  'aria-label': label = 'Analytics chart',
}) {
  const rows = new Map();
  data.forEach((series, index) =>
    (series.data || []).forEach((point) => {
      if (!rows.has(point.label)) rows.set(point.label, { label: point.label });
      rows.get(point.label)[`series${index}`] = point.value || 0;
    })
  );
  if (!rows.size)
    return (
      <div style={{ height }} className="flex items-center justify-center text-bun-body text-bun-stone">
        No data
      </div>
    );
  return (
    <div
      role={onPointClick ? 'group' : 'img'}
      aria-label={label}
      className="min-w-0 w-full text-bun-meta font-instrument text-bun-stone"
    >
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={Array.from(rows.values())}
          margin={{ top: 12, right: 8, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="var(--bun-line-soft)" vertical={false} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'var(--bun-stone)', fontFamily: BUN_CHART_FONT, fontSize: 11 }}
            minTickGap={24}
            tickFormatter={xTickFormatter}
          />
          <YAxis
            width={48}
            axisLine={false}
            tickLine={false}
            tickFormatter={yAxisFormatter}
            domain={yDomain}
            tick={{ fill: 'var(--bun-stone)', fontFamily: BUN_CHART_FONT, fontSize: 11 }}
          />
          <Tooltip
            content={tooltipRows ? ({ active, label: pointLabel }) => active ? <ChartTooltip title={labelFormatter ? labelFormatter(pointLabel) : pointLabel} rows={tooltipRows(pointLabel)} valueFormatter={valueFormatter} /> : null : undefined}
            formatter={valueFormatter}
            labelFormatter={labelFormatter}
            contentStyle={{
              background: 'var(--bun-ink)',
              border: 'none',
              borderRadius: 8,
              color: 'var(--bun-on-ink)',
              fontFamily: BUN_CHART_FONT,
            }}
            itemStyle={{ color: 'var(--bun-on-ink)' }}
            cursor={{ stroke: 'var(--bun-line)' }}
          />
          {showLegend && <Legend iconSize={8} wrapperStyle={{ fontFamily: BUN_CHART_FONT, fontSize: 11 }} />}
          {referenceLines.map((reference, index) => (
            <ReferenceLine
              key={index}
              x={reference.x}
              y={reference.y}
              stroke={reference.color || 'var(--bun-stone)'}
              strokeDasharray="4 4"
              label={reference.label ? { value: reference.label, fill: reference.color || 'var(--bun-stone)', fontFamily: BUN_CHART_FONT, fontSize: 11, position: reference.position || 'insideTopRight' } : undefined}
            />
          ))}
          {referenceDots.map((reference, index) => <ReferenceDot key={index} x={reference.x} y={reference.y} r={4} stroke={reference.color || 'var(--bun-stone)'} strokeWidth={2} fill={reference.hollow ? 'var(--bun-surface)' : reference.color || 'var(--bun-stone)'} />)}
          {data.map((series, index) =>
            (series.type || type) === 'bar' ? (
              <Bar
                key={index}
                dataKey={`series${index}`}
                name={series.label}
                fill={series.color || COLORS[index % COLORS.length]}
                stackId={series.stackId !== undefined ? series.stackId : stacked ? 'total' : undefined}
                isAnimationActive={false}
                onClick={onPointClick ? (point) => onPointClick({ seriesIndex: index, label: point.label, value: point[`series${index}`] }) : undefined}
                cursor={onPointClick ? 'pointer' : undefined}
              />
            ) : (series.type || type) === 'area' ? (
              <Area
                key={index}
                dataKey={`series${index}`}
                name={series.label}
                stroke={series.color || COLORS[index % COLORS.length]}
                fill={series.color || COLORS[index % COLORS.length]}
                fillOpacity={series.fillOpacity ?? 0.3}
                stackId={series.stackId !== undefined ? series.stackId : stacked ? 'total' : undefined}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={index}
                dataKey={`series${index}`}
                name={series.label}
                stroke={series.color || COLORS[index % COLORS.length]}
                strokeWidth={2}
                strokeDasharray={series.strokeDasharray ?? (index < 3 ? undefined : `${2 + Math.floor(index / 3) * 2} 3`)}
                dot={false}
                isAnimationActive={false}
              />
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
      {onPointClick && type === 'bar' && <div className="sr-only focus-within:not-sr-only focus-within:flex focus-within:flex-wrap focus-within:gap-2">
        {data.flatMap((series, seriesIndex) => (series.data || []).map((point, pointIndex) => <button
          key={`${seriesIndex}-${pointIndex}`}
          type="button"
          className="min-h-11 rounded-bun border border-bun-line bg-bun-surface px-3 text-bun-meta text-bun-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue"
          onClick={() => onPointClick({ seriesIndex, label: point.label, value: point.value })}
        >{series.label}, {point.label}: {valueFormatter ? valueFormatter(point.value) : point.value}</button>))}
      </div>}
    </div>
  );
}
